import { useCallback, useRef } from 'react';
import { useBlocker, Location } from 'react-router-dom';
import { useUserSettings } from '../utils/use-user-settings';
import { isFeatureEnabled } from '../utils/feature-flags';
import UnsavedChangesDialogOld from './unsaved-changes-dialog-old';
import SharedDialog from './shared-dialog';

export const useUnsavedChanges = (isDirty: boolean) => {
  const bypassRef = useRef(false);
  const isCancelActionRef = useRef(false);

  const blocker = useBlocker(
    useCallback(
      ({
        currentLocation,
        nextLocation,
      }: {
        currentLocation: Location;
        nextLocation: Location;
      }) =>
        !bypassRef.current &&
        isDirty &&
        currentLocation.pathname !== nextLocation.pathname,
      [isDirty]
    )
  );
  const showDialog = blocker.state === 'blocked';

  // if we ever need to handle tab/window close
  // useEffect(() => {
  //   const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  //     if (isDirty) {
  //       event.preventDefault();
  //       event.returnValue = '';
  //     }
  //   };
  //   window.addEventListener('beforeunload', handleBeforeUnload);
  //   return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  // }, [isDirty]);

  const handleConfirm = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  const handleCancel = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  const bypass = useCallback(() => {
    bypassRef.current = true;
  }, []);

  if (blocker.state === 'blocked') {
    isCancelActionRef.current =
      blocker.location?.state?.fromCancelButton === true;
  }

  return {
    showDialog,
    handleConfirm,
    handleCancel,
    isCancelAction: isCancelActionRef.current,
    bypass,
  };
};

interface UnsavedChangesDialogProps {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
  entityName?: string;
  pageMode?: 'create' | 'edit' | 'clone' | 'update' | string;
  isCancelAction?: boolean;
}

export default function UnsavedChangesDialog({
  open,
  handleClose,
  handleConfirm,
  entityName = 'item',
  pageMode = 'create',
  isCancelAction = false,
}: UnsavedChangesDialogProps) {
  let title = 'Discard Unsaved Changes';
  let content =
    'You have unsaved changes. Are you sure you want to leave this page?';
  let confirmText = 'Discard';
  const { features } = useUserSettings();

  if (isCancelAction) {
    if (pageMode === 'edit' || pageMode === 'update') {
      title = 'Revert changes?';
      content = `Are you sure you want to revert to the last saved version of this ${entityName.toLowerCase()}? All changes made in this session will be lost.`;
      confirmText = 'Discard Changes';
    } else {
      title = `Cancel ${entityName} Creation`;
      content =
        'You have unsaved changes. Discarding will delete your progress.';
    }
  }

  if (!isFeatureEnabled('uiEnhancementPhase2', features)) {
    return (
      <UnsavedChangesDialogOld
        open={open}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        entityName={entityName}
        pageMode={pageMode}
        isCancelAction={isCancelAction}
      />
    );
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={title}
      dialogContent={content}
      secondaryButton={{
        title: 'Continue Editing',
        action: handleClose,
        loading: false,
        disabled: false,
      }}
      primaryButton={{
        title: confirmText,
        action: handleConfirm,
        loading: false,
        disabled: false,
        color: 'error',
      }}
      noCancel
    />
  );
}
